import { useMemo } from 'react';
import { sum } from 'd3';
import { Client } from 'urql';

import { graphql } from '@/__generated__';
import { useCacheMonitor } from '@/context/DiscoveryUrqlProvider';
import { useEnvironmentScope } from '../useEnvironmentScope';
import { useQueryStable } from '../useQueryStable';

export const GetResourceCounts = graphql(/* GraphQL */ `
  query ResourceCounts($environmentId: BigInt!, $userId: BigInt) {
    environment(id: $environmentId, userId: $userId) {
      applied {
        lastUpdatedAt
        resourceCounts
      }
      definition {
        lastUpdatedAt
        resourceCounts
      }
    }
  }
`);

export const getResourceCountsForEnvironment = async (
  environmentId: number,
  userId: number | null = null,
  client: Client,
): Promise<Record<string, number> | null> => {
  try {
    const result = await client
      .query(GetResourceCounts, { environmentId, userId })
      .toPromise();

    if (result.error) {
      return null;
    }

    if (!result.data) {
      return null;
    }

    const applied = result.data.environment.applied;
    const definition = result.data.environment.definition;

    if (!applied || !definition) {
      return null;
    }

    return {
      exposure: applied.resourceCounts?.exposures ?? 0,
      model: applied.resourceCounts?.models ?? 0,
      source: applied.resourceCounts?.sources ?? 0,
      seed: applied.resourceCounts?.seeds ?? 0,
      snapshot: applied.resourceCounts?.snapshots ?? 0,
      test: applied.resourceCounts?.tests ?? 0,
      groups: definition.resourceCounts?.groups ?? 0,
      macro: definition.resourceCounts?.macros ?? 0,
      metric: definition.resourceCounts?.metrics ?? 0,
      semantic_model: definition.resourceCounts?.semanticModels ?? 0,
      saved_query: definition.resourceCounts?.savedQueries ?? 0,
    };
  } catch (error) {
    return null;
  }
};

type ResourceCountProps = {
  environmentId: number | undefined;
  pause?: boolean;
};

export const useResourceCounts = ({ environmentId = 0, pause }: ResourceCountProps) => {
  const { userId } = useEnvironmentScope();
  const [query] = useQueryStable({
    query: GetResourceCounts,
    variables: { environmentId, userId: userId as number | null },
    pause: pause || !environmentId || userId === 'loading',
  });

  useCacheMonitor({
    applied: query.data?.environment.applied?.lastUpdatedAt,
    definition: query.data?.environment.definition?.lastUpdatedAt,
  });

  return useMemo(() => {
    const applied = query.data?.environment.applied;
    const definition = query.data?.environment.definition;

    const data =
      applied && definition && !query.fetching
        ? {
            exposure: applied.resourceCounts?.exposures ?? 0,
            model: applied.resourceCounts?.models ?? 0,
            source: applied.resourceCounts?.sources ?? 0,
            seed: applied.resourceCounts?.seeds ?? 0,
            snapshot: applied.resourceCounts?.snapshots ?? 0,
            test: applied.resourceCounts?.tests ?? 0,
            groups: definition.resourceCounts?.groups ?? 0,
            macro: definition.resourceCounts?.macros ?? 0,
            metric: definition.resourceCounts?.metrics ?? 0,
            semantic_model: definition.resourceCounts?.semanticModels ?? 0,
            saved_query: definition.resourceCounts?.savedQueries ?? 0,
          }
        : null;

    if (!data) {
      return { ...query, data: null };
    }

    const appliedCounts = sum(
      Object.values(applied?.resourceCounts ?? {}).map((count) =>
        typeof count === 'number' ? count : 0,
      ),
    );

    return {
      ...query,
      data: { ...data, appliedCounts },
    };
  }, [query]);
};

/**
 * If true, we found 0 applied resources in the Discovery API for this environment.
 */
export const useHasNoMetadata = ({ environmentId = 0, pause }: ResourceCountProps) => {
  const resourceCountsQuery = useResourceCounts({ environmentId, pause });

  const isMissingMetadata =
    resourceCountsQuery.data?.appliedCounts === 0 ||
    resourceCountsQuery.error?.graphQLErrors.some(
      (x) => x.extensions?.code === 'NO_DATA_AVAILABLE',
    );
  return isMissingMetadata;
};
